/* global Promise */

import parseItemResultXml from '../qti/parser/itemResult/ItemResultParser';
import stateCacheService from '../services/StateCache';
import sessionReader from '../store/readHelpers/SessionReader';
import apiService from './api/ApiService';
import { reportError } from './ErrorReporting';
import storeManager from '../store/StoreManager';
import itemFlagActions from '../actions/ItemFlagActions';
import responseSendingQueue from './ResponseSendingQueue';
import responseActions from '../actions/ResponseActions';
import questionSelectionActions from '../actions/QuestionSelectionActions';
import questionSelectionService from './QuestionSelectionService';
import reloginService from './ReloginService';
import backgroundAudioStatus from '../constants/BackgroundAudioStatus';
import backgroundAudioActions from '../actions/BackgroundAudioActions';
import backgroundAudioService from './BackgroundAudioService';

const stateRestorationService = {
    /**
     * Gets any user responses stored on the server.
     *
     * @returns {object}
     */
    getRemotelyStoredQuestionResponses() {
        const isPreview = sessionReader.isContextPreview();
        if (isPreview === false) {
            return apiService.fetchUserResponses()
                .then((remoteUserResponses) => {
                    let includeEmptyResponses = false;
                    if (questionSelectionService.isCandidateSelectedContentBlockEnabled()) {
                        includeEmptyResponses = true;
                    }
                    return this.parseItemResultXMLs(remoteUserResponses.questions, includeEmptyResponses);
                });
        }

        return Promise.resolve({});
    },

    getMostRecentItemResults(localItemResults, remoteItemResults) {
        const itemResultsMap = {};
        const outdatedOnServer = [];

        try {
            remoteItemResults.forEach((itemResult) => {
                itemResultsMap[itemResult.identifier] = itemResult;
            });

            localItemResults.forEach((itemResult) => {
                const { identifier, datestamp, responseIdentifiers } = itemResult;
                const remoteItemResult = itemResultsMap[identifier];

                const hasPendingSync = Array.isArray(responseIdentifiers) && responseIdentifiers.some(item =>
                    item.identifier.endsWith('_pendingSync') && item.userResponse?.[0] === 'true'
                );

                if (!remoteItemResult || remoteItemResult.datestamp < datestamp) {
                    const finalItemResult = { ...itemResult };
                    if (remoteItemResult?.presignedUrls) {
                        finalItemResult.presignedUrls = remoteItemResult.presignedUrls;
                    }
                    itemResultsMap[identifier] = finalItemResult;
                    outdatedOnServer.push(parseInt(identifier, 10));
                } else if (hasPendingSync && remoteItemResult) {
                    const offlineDataIdentifiers = responseIdentifiers.filter(item =>
                        item.identifier.endsWith('_drawingBase64')
                        || item.identifier.endsWith('_jsonData')
                        || item.identifier.endsWith('_pendingSync')
                    );
                    const mergedItemResult = { ...remoteItemResult };
                    const identifierMap = new Map(mergedItemResult.responseIdentifiers.map(ri => [ri.identifier, ri]));
                    for (const offlineItem of offlineDataIdentifiers) {
                        identifierMap.set(offlineItem.identifier, offlineItem);
                    }
                    mergedItemResult.responseIdentifiers = Array.from(identifierMap.values());
                    if (remoteItemResult.presignedUrls) {
                        mergedItemResult.presignedUrls = remoteItemResult.presignedUrls;
                    }
                    itemResultsMap[identifier] = mergedItemResult;
                }
            });
        } catch (ex) {
            reportError(ex);
        }

        return { itemResults: itemResultsMap, outdatedOnServer };
    },

    isEmptyItemResult(parsedItemResult) {
        const { responseIdentifiers, studentHighlights, questionAnnotations } = parsedItemResult;

        if (Array.isArray(studentHighlights) && studentHighlights.length > 0) {
            return false;
        }

        if (Array.isArray(questionAnnotations) && questionAnnotations.length > 0) {
            return false;
        }

        if (responseIdentifiers.length === 0) {
            return true;
        }

        if (responseIdentifiers.length === 1 &&
            responseIdentifiers[0] &&
            responseIdentifiers[0].identifier === 'RESPONSE_EXTERNAL_DELIVERY') {
            return true;
        }

        return parsedItemResult.duration === 0;
    },

    /**
     * @param {object} responses
     * @param {boolean} includeEmpty
     * @returns {Array}
     */
    parseItemResultXMLs(responses, includeEmpty = false) {
        const parsedResponses = Object.keys(responses)
            .map(key => parseInt(key, 10))
            .map(intKey => this.parseResponseItem(responses[intKey], includeEmpty))
            .filter(parsedItemResult => !!parsedItemResult);

        return parsedResponses;
    },

    parseResponseItem(response, includeEmpty) {
        const {
            commitAnswerTimestamp,
            itemResultXML,
            nextQuestionId,
            presignedUrls,
        } = response;

        const parsedItemResult = parseItemResultXml(itemResultXML);

        if (!commitAnswerTimestamp && !includeEmpty && this.isEmptyItemResult(parsedItemResult)) {
            return null;
        }

        if (Object.prototype.hasOwnProperty.call(response, 'selected')) {
            parsedItemResult.selected = response.selected;
        }

        if (presignedUrls && typeof presignedUrls === 'object') {
            parsedItemResult.presignedUrls = presignedUrls;
        }

        if (reloginService.isReloginRulesContext()) {
            parsedItemResult.nextQuestionId = nextQuestionId;
        }

        if (commitAnswerTimestamp) {
            // restore commitAnswerTimestamp if it's provided
            parsedItemResult.commitAnswerTimestamp = commitAnswerTimestamp;
            parsedItemResult.commitAnswerStatus = responseActions.commitTimestampStatus.STORED;
        }

        return parsedItemResult;
    },

    /**
     * @param {object} responses
     * @param {boolean} includeEmpty
     * @returns {Array}
     */
    parseItemResultXMLsAsMap(responses) {
        const keys = Object.keys(responses);
        const parsed = {};

        keys.forEach((key) => {
            try {
                const intKey = parseInt(key, 10);
                const responseData = responses[intKey];

                const {
                    itemResultXML,
                    commitAnswerTimestamp,
                    presignedUrls,
                } = responseData;

                const parsedItemResult = parseItemResultXml(itemResultXML);
                if (Object.prototype.hasOwnProperty.call(responses[intKey], 'selected')) {
                    parsedItemResult.selected = responses[intKey].selected;
                }

                if (presignedUrls && typeof presignedUrls === 'object') {
                    parsedItemResult.presignedUrls = presignedUrls;
                }

                if (commitAnswerTimestamp) {
                    // restore commitAnswerTimestamp if it's provided
                    parsedItemResult.commitAnswerTimestamp = commitAnswerTimestamp;
                    parsedItemResult.commitAnswerStatus = responseActions.commitTimestampStatus.STORED;
                }

                parsed[intKey] = parsedItemResult;
            } catch (error) {
                window.console.error(error);
            }
        });

        return parsed;
    },

    async loadSavedResponsesForExam() {
        const responses = await this.getSavedQuestionResponses(sessionReader.getPartyId());
        const { itemResults, outdatedOnServer = [] } = responses;

        if (outdatedOnServer.length) {
            const queue = responseSendingQueue.getInstance();
            window.console.log('Adding responses to queue from loadSavedResponsesForExam');
            queue.queueResponses(outdatedOnServer);
        }

        storeManager.dispatch(
            responseActions.setResponseState(itemResults),
        );

        this.restoreCandidateSelectedQuestions(itemResults);
        this.restoreBackgroundAudioState();
    },

    getSavedQuestionResponses(partyId) {
        return this.getRemotelyStoredQuestionResponses(partyId)
            .then((remoteItemResults) => {
                if (sessionReader.isContextExam()) {
                    const stateCache = stateCacheService.getStateCacheInstance();
                    const localItemResults = stateCache.getAllQuestionResponses();
                    return this.getMostRecentItemResults(localItemResults, remoteItemResults);
                }

                return { itemResults: remoteItemResults, outdatedOnServer: [] };
            });
    },

    restoreSavedQuestionFlags() {
        const store = storeManager.getStore();
        const stateCache = stateCacheService.getStateCacheInstance();
        const flaggedItems = stateCache.getFlaggedItems();

        store.dispatch(
            itemFlagActions.setFlaggedItems(flaggedItems),
        );

        return Promise.resolve(flaggedItems);
    },

    restoreCandidateSelectedQuestions(userResponseQuestions) {
        if (questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled()) {
            const stateQuestionSelection = questionSelectionService.mapServerToClientStateQuestionSelection(userResponseQuestions);
            storeManager.dispatch(
                questionSelectionActions.restoreQuestionsSelection(stateQuestionSelection)
            );
        }
    },

    restoreBackgroundAudioState() {
        if (!backgroundAudioService.isBackgroundAudioControllable()) {
            return;
        }

        const stateCache = stateCacheService.getStateCacheInstance();
        const isIntoPlayed = stateCache.isBackgroundAudioIntroPlayed();
        const allStatuses = stateCache.getAllSectionAudioStatuses();
        const allPositions = stateCache.getAllSectionAudioPositions();
        const allTimesReplayRequested = stateCache.getAllSectionAudioTimesReplayRequested();
        const restoredBackgroundAudio = {
            isIntoPlayed,
        };

        Object.keys(allStatuses).forEach((sectionId) => {
            const status = allStatuses[sectionId] === backgroundAudioStatus.PLAYING ? backgroundAudioStatus.PAUSED : allStatuses[sectionId];
            restoredBackgroundAudio[sectionId] = {
                status,
                position: allPositions[sectionId] || null,
                timesReplayRequested: allTimesReplayRequested[sectionId] || 0,
            };
        });

        storeManager.dispatch(backgroundAudioActions.restoreAudioStatus(restoredBackgroundAudio));
    },
};

export default stateRestorationService;
